import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import Header from '../components/Header';
import InteractiveMaterial from '../components/InteractiveMaterial';
import { parseMaterialTags, buildTagFromSelection, buildInputTagFromSelection, buildOrderTagFromSelection, parseMaterialFileContent, createEmptyMaterialProgress, normalizeMaterialProgress } from '../utils/materialUtils';
import './Classroom.css';

// Настройка worker для PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Отдельный мемоизированный компонент для PDF, чтобы избежать перерендера при изменении pan/scale
const PdfViewerComponent = React.forwardRef(({ file, pageNumber, pageWidth, onLoadSuccess, onLoadError }, ref) => {
  const documentOptions = useMemo(() => ({
    httpHeaders: {
      'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
    },
    cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
    cMapPacked: true,
  }), []);

  return (
    <div 
      ref={ref}
      className="pdf-background"
      style={{
        transformOrigin: 'top left',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1,
      }}
    >
      <Document
        file={file}
        onLoadSuccess={onLoadSuccess}
        onLoadError={onLoadError}
        loading={<div>Загрузка PDF...</div>}
        error={<div>Ошибка загрузки PDF</div>}
        className="pdf-document"
        options={documentOptions}
      >
        <Page 
          pageNumber={pageNumber} 
          width={pageWidth}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          loading={<div style={{ padding: '2rem', textAlign: 'center' }}>Загрузка страницы...</div>}
          style={{ display: 'block', visibility: 'visible', opacity: 1 }}
        />
      </Document>
    </div>
  );
});

const PdfViewer = React.memo(PdfViewerComponent, (prevProps, nextProps) => {
  // Перерендериваем только если изменился file, pageNumber или pageWidth
  // Игнорируем изменения в onLoadSuccess и onLoadError (они мемоизированы через useCallback)
  return prevProps.file === nextProps.file && 
         prevProps.pageNumber === nextProps.pageNumber && 
         prevProps.pageWidth === nextProps.pageWidth;
});

const Classroom = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lessonFiles, setLessonFiles] = useState([]);
  const [lessonMaterials, setLessonMaterials] = useState([]);
  const lessonMaterialsRef = useRef([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const selectedMaterialRef = useRef(null);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [newMaterialTitle, setNewMaterialTitle] = useState('');
  const [newMaterialText, setNewMaterialText] = useState('');
  const materialTextareaRef = useRef(null);
  const materialFileInputRef = useRef(null);
  const pendingMaterialIdRef = useRef(null);
  const [contentTab, setContentTab] = useState('materials');
  const contentTabRef = useRef('materials');
  const [selectedFile, setSelectedFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfPageWidth, setPdfPageWidth] = useState(600); // Ширина страницы PDF
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [selectedImage, setSelectedImage] = useState(null); // Для модального окна
  const imageInputRef = useRef(null);
  
  // Обработчик ESC для закрытия модального окна
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedImage) {
        setSelectedImage(null);
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [selectedImage]);
  
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  
  // PDF state для синхронизации
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfPanX, setPdfPanX] = useState(0);
  const [pdfPanY, setPdfPanY] = useState(0);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingTool, setDrawingTool] = useState(null); // 'pen', 'eraser', null
  const [drawingColor, setDrawingColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(2);
  const [eraserSize, setEraserSize] = useState(20); // Размер ластика по умолчанию
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 }); // Позиция мыши для индикатора ластика
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfBackgroundRef = useRef(null); // Ref для элемента PDF background
  const pdfWrapperRef = useRef(null); // Ref для pdf-wrapper элемента
  const isDrawingRef = useRef(false);
  const lastDrawPointRef = useRef({ x: 0, y: 0 });
  const startPointRef = useRef({ x: 0, y: 0 }); // Начальная точка для проверки простого клика
  const hasMovedRef = useRef(false); // Флаг, было ли движение мыши
  const isSyncingRef = useRef(false); // Предотвращаем циклическую синхронизацию
  const isMaterialProgressSyncingRef = useRef(false);
  const materialProgressMapRef = useRef({});
  const materialProgressRef = useRef(createEmptyMaterialProgress());
  const saveProgressTimeoutRef = useRef(null);
  const userRef = useRef(user);
  const applyMaterialSelectionFromSyncRef = useRef(() => {});
  const applyMaterialProgressRef = useRef(() => {});
  const [materialProgress, setMaterialProgress] = useState(createEmptyMaterialProgress());
  const remoteDrawingStateRef = useRef({}); // Состояние для удаленного рисования (путь для каждого пользователя)
  const canvasHistoryRef = useRef([]); // История состояний canvas для отмены
  const maxHistorySize = 50; // Максимальный размер истории

  // Обновляем localVideoRef при изменении localStream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Обновляем remoteVideoRef при изменении remoteStream
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  useEffect(() => {
    contentTabRef.current = contentTab;
  }, [contentTab]);

  useEffect(() => {
    lessonMaterialsRef.current = lessonMaterials;
  }, [lessonMaterials]);

  useEffect(() => {
    selectedMaterialRef.current = selectedMaterial;
  }, [selectedMaterial]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    materialProgressRef.current = materialProgress;
  }, [materialProgress]);

  const requestMaterialProgress = useCallback((materialId) => {
    if (!materialId || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: 'material_progress_request',
      materialId,
    }));
  }, []);

  const syncMaterialProgress = useCallback((materialId, state) => {
    if (!materialId || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: 'material_progress',
      materialId,
      state,
    }));
  }, []);

  const scheduleSaveStudentProgress = useCallback((materialId, state) => {
    if (userRef.current?.user_type !== 'student' || !materialId) return;
    if (saveProgressTimeoutRef.current) {
      clearTimeout(saveProgressTimeoutRef.current);
    }
    saveProgressTimeoutRef.current = setTimeout(async () => {
      try {
        await api.put(`/lessons/materials/${materialId}/my-submission/`, { progress: state });
      } catch (error) {
        console.error('Ошибка сохранения ответов студента:', error);
      }
    }, 800);
  }, []);

  const applyMaterialProgress = useCallback((materialId, state) => {
    if (!materialId || !state) return;
    const normalizedState = normalizeMaterialProgress(state);
    materialProgressMapRef.current[materialId] = normalizedState;
    if (selectedMaterialRef.current?.id === materialId) {
      isMaterialProgressSyncingRef.current = true;
      setMaterialProgress(normalizedState);
      window.setTimeout(() => {
        isMaterialProgressSyncingRef.current = false;
      }, 0);
    }
    if (userRef.current?.user_type === 'student') {
      scheduleSaveStudentProgress(materialId, normalizedState);
    }
  }, [scheduleSaveStudentProgress]);

  const fetchStudentMaterialSubmission = useCallback(async (materialId) => {
    if (userRef.current?.user_type !== 'student' || !materialId) return null;
    try {
      const response = await api.get(`/lessons/materials/${materialId}/my-submission/`);
      const progress = normalizeMaterialProgress(response.data?.progress);
      materialProgressMapRef.current[materialId] = progress;
      if (selectedMaterialRef.current?.id === materialId) {
        isMaterialProgressSyncingRef.current = true;
        setMaterialProgress(progress);
        isMaterialProgressSyncingRef.current = false;
      }
      return progress;
    } catch (error) {
      console.error('Ошибка загрузки ответов студента:', error);
      return null;
    }
  }, []);

  const handleMaterialProgressChange = useCallback((state) => {
    if (isMaterialProgressSyncingRef.current) return;
    const materialId = selectedMaterialRef.current?.id;
    if (!materialId) return;

    const normalizedState = normalizeMaterialProgress(state);
    if (JSON.stringify(normalizedState) === JSON.stringify(materialProgressRef.current)) {
      return;
    }

    materialProgressMapRef.current[materialId] = normalizedState;
    setMaterialProgress(normalizedState);
    syncMaterialProgress(materialId, normalizedState);
    scheduleSaveStudentProgress(materialId, normalizedState);
  }, [syncMaterialProgress, scheduleSaveStudentProgress]);

  const loadMaterialProgress = useCallback((materialId) => {
    if (userRef.current?.user_type === 'student') {
      fetchStudentMaterialSubmission(materialId);
      const cached = materialProgressMapRef.current[materialId] || createEmptyMaterialProgress();
      setMaterialProgress(cached);
      return cached;
    }
    const saved = materialProgressMapRef.current[materialId] || createEmptyMaterialProgress();
    setMaterialProgress(saved);
    return saved;
  }, [fetchStudentMaterialSubmission]);

  useEffect(() => {
    if (!pendingMaterialIdRef.current || lessonMaterials.length === 0) return;
    const pendingId = pendingMaterialIdRef.current;
    const foundMaterial = lessonMaterials.find(
      (item) => String(item.id) === String(pendingId)
    );
    if (foundMaterial) {
      setSelectedMaterial(foundMaterial);
      loadMaterialProgress(foundMaterial.id);
      requestMaterialProgress(foundMaterial.id);
      pendingMaterialIdRef.current = null;
    }
  }, [lessonMaterials, loadMaterialProgress, requestMaterialProgress]);

  const switchContentTab = useCallback((tab) => {
    if (user?.user_type !== 'teacher') return;
    setContentTab(tab);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'content_tab', tab }));
    }
  }, [user?.user_type]);

  useEffect(() => {
    fetchLessonMaterials();
    if (user?.user_type === 'teacher') {
      fetchLessonFiles();
    }

    // Запрашиваем камеру сразу, независимо от WebSocket
    setupWebRTC();
    setupWebSocket();
  }, [user, teacherId]);

  // Cleanup при размонтировании компонента - останавливаем все потоки
  useEffect(() => {
    return () => {
      console.log('Очистка ресурсов при выходе из классрума...');

      if (saveProgressTimeoutRef.current) {
        clearTimeout(saveProgressTimeoutRef.current);
      }
      
      // Останавливаем локальный поток (используем ref для актуального значения)
      const currentLocalStream = localStreamRef.current;
      if (currentLocalStream) {
        console.log('Остановка локального потока...');
        currentLocalStream.getTracks().forEach(track => {
          track.stop();
          console.log('Трек остановлен:', track.kind);
        });
        localStreamRef.current = null;
      }
      
      // Останавливаем удаленный поток (используем ref для актуального значения)
      const currentRemoteStream = remoteStreamRef.current;
      if (currentRemoteStream) {
        console.log('Остановка удаленного потока...');
        currentRemoteStream.getTracks().forEach(track => {
          track.stop();
          console.log('Трек остановлен:', track.kind);
        });
        remoteStreamRef.current = null;
      }
      
      // Закрываем peer connection
      if (peerConnectionRef.current) {
        console.log('Закрытие peer connection...');
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      // Закрываем WebSocket
      if (wsRef.current) {
        console.log('Закрытие WebSocket...');
        wsRef.current.close();
        wsRef.current = null;
      }
      
      // Очищаем video элементы
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
    };
  }, []); // Пустой массив зависимостей - выполняется только при размонтировании

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Добавляем обработчик wheel события с passive: false для предотвращения ошибки
  useEffect(() => {
    const pdfWrapper = pdfContainerRef.current?.querySelector('.pdf-wrapper');
    if (!pdfWrapper || user?.user_type !== 'teacher') return;

    const handleWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const newScale = Math.max(0.5, Math.min(3.0, pdfScale + delta));
        setPdfScale(newScale);
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isSyncingRef.current) {
          wsRef.current.send(JSON.stringify({
            type: 'pdf_state',
            page: pageNumber,
            scale: newScale,
            panX: pdfPanX,
            panY: pdfPanY,
          }));
        }
      } else if (e.shiftKey) {
        e.preventDefault();
        const deltaX = e.deltaY > 0 ? 10 : -10;
        const newPanX = pdfPanX + deltaX;
        setPdfPanX(newPanX);
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isSyncingRef.current) {
          wsRef.current.send(JSON.stringify({
            type: 'pdf_state',
            page: pageNumber,
            scale: pdfScale,
            panX: newPanX,
            panY: pdfPanY,
          }));
        }
      } else {
        e.preventDefault();
        const deltaY = e.deltaY > 0 ? 10 : -10;
        const newPanY = pdfPanY + deltaY;
        setPdfPanY(newPanY);
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isSyncingRef.current) {
          wsRef.current.send(JSON.stringify({
            type: 'pdf_state',
            page: pageNumber,
            scale: pdfScale,
            panX: pdfPanX,
            panY: newPanY,
          }));
        }
      }
    };

    pdfWrapper.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      pdfWrapper.removeEventListener('wheel', handleWheel);
    };
  }, [user?.user_type, pdfScale, pdfPanX, pdfPanY, pageNumber]);

  // Обновление transform через DOM для избежания перерендера PDF
  useEffect(() => {
    if (pdfBackgroundRef.current) {
      pdfBackgroundRef.current.style.transform = `scale(${pdfScale}) translate(${pdfPanX + 20}px, ${pdfPanY + 20}px)`;
    }
  }, [pdfScale, pdfPanX, pdfPanY]);

  // Обновление transform для canvas
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.style.transform = `scale(${pdfScale}) translate(${pdfPanX}px, ${pdfPanY}px)`;
    }
  }, [pdfScale, pdfPanX, pdfPanY]);

  // Инициализация canvas при загрузке PDF или смене страницы
  useEffect(() => {
    if (canvasRef.current && selectedFile && pdfContainerRef.current) {
      const canvas = canvasRef.current;
      // Устанавливаем размеры canvas - делаем его намного больше для увеличения области рисования
      // Canvas должен быть достаточно большим для PDF с большим padding
      const pageWidth = Math.min(pdfContainerRef.current.offsetWidth - 40, 800);
      const pageHeight = pageWidth * 1.414; // A4 соотношение сторон
      const padding = 100; // Увеличенный отступ со всех сторон для большей области рисования
      // Увеличиваем canvas в 2 раза для большей области рисования
      canvas.width = (pageWidth + (padding * 2)) * 2;
      canvas.height = (pageHeight + (padding * 2)) * 2;
      
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = drawingColor;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Очищаем canvas при смене страницы или файла
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Очищаем историю при смене страницы или файла
      canvasHistoryRef.current = [];
    }
  }, [selectedFile, pageNumber]); // Убрали drawingColor и lineWidth, чтобы canvas не переинициализировался при смене цвета

  // Обновление canvas при изменении цвета или толщины линии
  // НЕ очищаем canvas - только обновляем настройки для будущего рисования
  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.strokeStyle = drawingColor;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [drawingColor, lineWidth]);

  const fetchLessonMaterials = async () => {
    try {
      const response = await api.get('/lessons/materials/', {
        params: { teacher: teacherId },
      });
      setLessonMaterials(response.data);
      return response.data;
    } catch (error) {
      console.error('Ошибка загрузки материалов урока:', error);
      return [];
    }
  };

  const applyMaterialSelectionFromSync = useCallback((materialId) => {
    if (!materialId) {
      setSelectedMaterial(null);
      pendingMaterialIdRef.current = null;
      return;
    }

    const foundMaterial = lessonMaterialsRef.current.find(
      (item) => String(item.id) === String(materialId)
    );

    if (foundMaterial) {
      setSelectedMaterial(foundMaterial);
      loadMaterialProgress(materialId);
      pendingMaterialIdRef.current = null;
      requestMaterialProgress(materialId);
      return;
    }

    pendingMaterialIdRef.current = materialId;
    fetchLessonMaterials();
  }, [loadMaterialProgress, requestMaterialProgress, teacherId]);

  useEffect(() => {
    applyMaterialSelectionFromSyncRef.current = applyMaterialSelectionFromSync;
  }, [applyMaterialSelectionFromSync]);

  useEffect(() => {
    applyMaterialProgressRef.current = applyMaterialProgress;
  }, [applyMaterialProgress]);

  const syncMaterialSelection = useCallback((material) => {
    if (user?.user_type !== 'teacher' || !material) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'material_selected',
        materialId: material.id,
        materialTitle: material.title,
      }));
    }
  }, [user?.user_type]);

  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
    loadMaterialProgress(material.id);
    syncMaterialSelection(material);
    requestMaterialProgress(material.id);
  };

  const handleMakeTagFromSelection = () => {
    const textarea = materialTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = textarea.value.substring(start, end).trim();

    if (!selected) {
      alert('Выделите слово или фразу в тексте');
      return;
    }

    const wrongOptions = window.prompt(
      'Неверные варианты (через запятую):',
      ''
    );
    if (wrongOptions === null) return;

    const tag = buildTagFromSelection(selected, wrongOptions);
    const updatedText = `${textarea.value.substring(0, start)}${tag}${textarea.value.substring(end)}`;
    setNewMaterialText(updatedText);
  };

  const handleMakeInputTagFromSelection = () => {
    const textarea = materialTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = textarea.value.substring(start, end).trim();

    if (!selected) {
      alert('Выделите слово или фразу в тексте');
      return;
    }

    const correctAnswer = window.prompt(
      `Правильная форма для «${selected}»:`,
      selected
    );
    if (correctAnswer === null) return;

    const trimmedAnswer = correctAnswer.trim();
    if (!trimmedAnswer) {
      alert('Введите правильную форму');
      return;
    }

    const tag = buildInputTagFromSelection(selected, trimmedAnswer);
    const updatedText = `${textarea.value.substring(0, start)}${tag}${textarea.value.substring(end)}`;
    setNewMaterialText(updatedText);
  };

  const handleMakeOrderTagFromSelection = () => {
    const textarea = materialTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = textarea.value.substring(start, end).trim();

    if (!selected) {
      alert('Выделите предложение в тексте');
      return;
    }

    const words = selected.split(/\s+/).filter(Boolean);
    if (words.length < 2) {
      alert('Для тега порядка нужно минимум 2 слова');
      return;
    }

    const tag = buildOrderTagFromSelection(selected);
    const updatedText = `${textarea.value.substring(0, start)}${tag}${textarea.value.substring(end)}`;
    setNewMaterialText(updatedText);
  };

  const handleMakeCheckboxTag = () => {
    const textarea = materialTextareaRef.current;
    const defaultTag = '@@*Правильный вариант 1|*Правильный вариант 2|Неправильный вариант@@';

    if (!textarea) {
      setNewMaterialText((prev) => `${prev}${prev ? '\n\n' : ''}${defaultTag}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const updatedText = `${textarea.value.substring(0, start)}${defaultTag}${textarea.value.substring(end)}`;
    setNewMaterialText(updatedText);
  };

  const handleMaterialFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.txt')) {
      alert('Загрузите текстовый файл (.txt)');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const { title, rawText } = parseMaterialFileContent(
        loadEvent.target?.result || '',
        file.name
      );

      if (!rawText) {
        alert('Файл пуст');
        return;
      }

      setNewMaterialTitle(title);
      setNewMaterialText(rawText);
      setShowMaterialForm(true);
    };
    reader.onerror = () => {
      alert('Не удалось прочитать файл');
    };
    reader.readAsText(file, 'UTF-8');
    event.target.value = '';
  };

  const handleMaterialSave = async () => {
    const title = newMaterialTitle.trim();
    const rawText = newMaterialText.trim();

    if (!title) {
      alert('Введите название материала');
      return;
    }
    if (!rawText) {
      alert('Введите текст материала');
      return;
    }

    const content = parseMaterialTags(rawText);
    if (content.length === 0) {
      alert('Не удалось разобрать текст материала');
      return;
    }

    try {
      const response = await api.post('/lessons/materials/', {
        title,
        raw_text: rawText,
        content,
      });
      const createdMaterial = response.data;
      setLessonMaterials((prev) => [createdMaterial, ...prev]);
      setNewMaterialTitle('');
      setNewMaterialText('');
      setShowMaterialForm(false);
      handleMaterialSelect(createdMaterial);
    } catch (error) {
      console.error('Ошибка сохранения материала:', error);
      alert('Не удалось сохранить материал');
    }
  };

  const handleMaterialDelete = async (materialId, event) => {
    event.stopPropagation();
    if (!window.confirm('Удалить материал?')) return;

    try {
      await api.delete(`/lessons/materials/${materialId}/`);
      setLessonMaterials((prev) => prev.filter((item) => item.id !== materialId));
      if (selectedMaterial?.id === materialId) {
        setSelectedMaterial(null);
      }
    } catch (error) {
      console.error('Ошибка удаления материала:', error);
      alert('Не удалось удалить материал');
    }
  };

  const fetchLessonFiles = async () => {
    try {
      const response = await api.get('/lessons/files/');
      console.log('Загружены файлы:', response.data);
      // Проверяем структуру файлов
      if (response.data && response.data.length > 0) {
        console.log('Пример файла:', response.data[0]);
        console.log('file.file:', response.data[0].file);
      }
      setLessonFiles(response.data);
    } catch (error) {
      console.error('Ошибка загрузки файлов:', error);
    }
  };

  // Функция для переключения видео
  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  };

  // Функция для переключения аудио
  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  };

  const setupWebRTC = async () => {
    try {
      console.log('Запрос доступа к камере...');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      console.log('Доступ к камере получен');
      setLocalStream(stream);
      localStreamRef.current = stream;
      setIsVideoEnabled(true);
      setIsAudioEnabled(true);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        console.log('Локальное видео установлено');
      }

      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
      });

      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      pc.ontrack = (event) => {
        console.log('Получен удаленный поток:', event);
        const remoteStream = event.streams[0];
        setRemoteStream(remoteStream);
        remoteStreamRef.current = remoteStream;
        // Обновляем видео элемент при получении потока
        setTimeout(() => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
            console.log('Удаленное видео установлено');
          }
        }, 100);
      };

      // Обработка ICE кандидатов
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'webrtc-ice-candidate',
            data: event.candidate,
          }));
        }
      };

      peerConnectionRef.current = pc;
      
      // Offer будет отправлен после открытия WebSocket (в setupWebSocket)
    } catch (error) {
      console.error('Ошибка настройки WebRTC:', error);
    }
  };

  const setupWebSocket = async () => {
    // Закрываем предыдущее соединение, если оно есть
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      console.log('Закрытие предыдущего WebSocket соединения');
      wsRef.current.close();
    }
    
    // Используем ID преподавателя для комнаты (одинаковый для ученика и преподавателя)
    // teacherId - это ID преподавателя, который передается в URL
    const roomName = `room_${teacherId}`;
    console.log('Создание комнаты:', roomName, 'для пользователя:', user?.username, 'тип:', user?.user_type);
    // Определяем протокол WebSocket на основе текущего протокола страницы
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    
    // Используем относительный путь для WebSocket в продакшене
    let wsHost;
    if (process.env.REACT_APP_WS_URL && process.env.NODE_ENV === 'development') {
      wsHost = process.env.REACT_APP_WS_URL;
      // Заменяем http:// на https:// если страница загружена по HTTPS
      if (wsHost.startsWith('http://') && window.location.protocol === 'https:') {
        wsHost = wsHost.replace('http://', 'https://').replace('ws://', 'wss://');
      }
      if (wsHost.startsWith('ws://') && window.location.protocol === 'https:') {
        wsHost = wsHost.replace('ws://', 'wss://');
      }
    } else {
      // В продакшене используем относительный путь с правильным протоколом
      wsHost = `${wsProtocol}//${window.location.host}`;
    }
    
    // Получаем токен для аутентификации
    const token = localStorage.getItem('token');
    // Формируем URL: /ws/chat/room_name/?token=...
    const wsUrl = `${wsHost}/ws/chat/${roomName}/${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    console.log('Подключение к WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = async () => {
      console.log('WebSocket connected');

      if (user?.user_type === 'teacher' && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'content_tab',
          tab: contentTabRef.current,
        }));
      }

      if (user?.user_type === 'student' && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'content_tab_request' }));
        wsRef.current.send(JSON.stringify({ type: 'material_selected_request' }));
      }

      if (user?.user_type === 'teacher' && selectedMaterialRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'material_selected',
          materialId: selectedMaterialRef.current.id,
          materialTitle: selectedMaterialRef.current.title,
        }));
        const savedProgress = materialProgressMapRef.current[selectedMaterialRef.current.id];
        if (savedProgress) {
          wsRef.current.send(JSON.stringify({
            type: 'material_progress',
            materialId: selectedMaterialRef.current.id,
            state: savedProgress,
          }));
        } else {
          wsRef.current.send(JSON.stringify({
            type: 'material_progress_request',
            materialId: selectedMaterialRef.current.id,
          }));
        }
      }
      
      // Если WebRTC еще не настроен, настраиваем его
      if (!peerConnectionRef.current) {
        await setupWebRTC();
      }
      
      // Отправляем offer (для преподавателя) после небольшой задержки
      if (user?.user_type === 'teacher' && peerConnectionRef.current) {
        setTimeout(async () => {
          try {
            const offer = await peerConnectionRef.current.createOffer();
            await peerConnectionRef.current.setLocalDescription(offer);
            
            // Используем wsRef.current для проверки состояния
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({
                type: 'webrtc-offer',
                data: offer,
              }));
            }
          } catch (error) {
            console.error('Ошибка создания offer:', error);
          }
        }, 500);
      }
    };
    
    ws.onmessage = async (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (error) {
        console.error('Ошибка разбора WebSocket-сообщения:', error);
        return;
      }

      try {
      if (data.type === 'content_tab' && user?.user_type === 'student') {
        setContentTab(data.tab === 'materials' ? 'materials' : 'files');
        return;
      }

      if (data.type === 'content_tab_request' && user?.user_type === 'teacher') {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'content_tab',
            tab: contentTabRef.current,
          }));
        }
        return;
      }

      if (data.type === 'material_selected') {
        applyMaterialSelectionFromSyncRef.current(data.materialId);
        if (user?.user_type === 'student') {
          setContentTab('materials');
        }
        return;
      }

      if (data.type === 'material_selected_request' && user?.user_type === 'teacher') {
        if (selectedMaterialRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'material_selected',
            materialId: selectedMaterialRef.current.id,
            materialTitle: selectedMaterialRef.current.title,
          }));
          const savedProgress = materialProgressMapRef.current[selectedMaterialRef.current.id];
          if (savedProgress) {
            wsRef.current.send(JSON.stringify({
              type: 'material_progress',
              materialId: selectedMaterialRef.current.id,
              state: savedProgress,
            }));
          }
        }
        return;
      }

      if (data.type === 'material_progress') {
        const sender = data.sender || '';
        if (sender === user?.username) return;
        applyMaterialProgressRef.current(data.materialId, data.state);
        return;
      }

      if (data.type === 'material_progress_request') {
        const materialId = data.materialId;
        const savedProgress = materialProgressMapRef.current[materialId];
        if (materialId && savedProgress && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'material_progress',
            materialId,
            state: savedProgress,
          }));
        }
        return;
      }
      
      // Если это сообщение о выборе файла
      if (data.type === 'file_selected') {
        console.log('Получено сообщение file_selected:', data);
        
        // Проверяем fileUrl, если null - пытаемся найти файл по fileName
        if (data.fileUrl) {
          let fileUrl = data.fileUrl;
          
          // Преобразуем абсолютный URL с localhost в относительный путь
          if (fileUrl.startsWith('http://localhost') || fileUrl.startsWith('https://localhost')) {
            try {
              const urlObj = new URL(fileUrl);
              fileUrl = urlObj.pathname + urlObj.search;
              console.log('Преобразован абсолютный URL localhost в относительный путь:', fileUrl);
            } catch (e) {
              console.error('Ошибка преобразования URL:', e);
              // Если не удалось распарсить, пытаемся извлечь путь вручную
              const match = fileUrl.match(/https?:\/\/[^\/]+(\/.*)/);
              if (match) {
                fileUrl = match[1];
                console.log('Извлечен путь из URL:', fileUrl);
              }
            }
          }
          
          // Если URL начинается с /media/, но у нас есть fileName, пытаемся использовать API endpoint
          if (fileUrl.startsWith('/media/') && data.fileName) {
            const foundFile = lessonFiles.find(f => f.name === data.fileName);
            if (foundFile && foundFile.id) {
              fileUrl = `/api/lessons/files/${foundFile.id}/download/`;
              console.log('Используем API endpoint вместо /media/ URL:', fileUrl);
            }
          }
          
          setSelectedFile(fileUrl);
          setPageNumber(1);
          setNumPages(null);
          setPdfScale(1.0);
          setPdfPanX(0);
          setPdfPanY(0);
          // Очищаем canvas при смене файла
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
          // Отправляем команду очистки через WebSocket
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'drawing',
              action: 'clear',
            }));
          }
        } else if (data.fileName) {
          // Если fileUrl null, но есть fileName, пытаемся найти файл в списке
          console.log('fileUrl null, пытаемся найти файл по имени:', data.fileName);
          
          // Пытаемся найти файл в списке lessonFiles для получения id
          const foundFile = lessonFiles.find(f => f.name === data.fileName);
          if (foundFile && foundFile.id) {
            // Используем API endpoint
            const apiUrl = `/api/lessons/files/${foundFile.id}/download/`;
            console.log('Найден файл в списке, используем API endpoint:', apiUrl);
            setSelectedFile(apiUrl);
          } else {
            // Fallback: используем прямой путь к медиа файлу
            console.log('Файл не найден в списке, используем fallback URL');
            const fallbackUrl = `/media/lessons/${encodeURIComponent(data.fileName)}`;
            setSelectedFile(fallbackUrl);
          }
          setPageNumber(1);
          setNumPages(null);
          setPdfScale(1.0);
          setPdfPanX(0);
          setPdfPanY(0);
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        } else {
          console.error('Не удалось получить информацию о файле из сообщения WebSocket');
        }
      } else if (data.type === 'pdf_state') {
        // Синхронизация состояния PDF от преподавателя
        // Ученик получает состояние от преподавателя, преподаватель игнорирует свои собственные сообщения
        const sender = data.sender || '';
        const currentUser = user?.username || '';
        
        console.log('Получено pdf_state:', { sender, currentUser, data, isSyncing: isSyncingRef.current });
        
        // Применяем изменения только если это не наше собственное сообщение
        if (sender !== currentUser && !isSyncingRef.current) {
          isSyncingRef.current = true;
          
          // Важно: используем data.page напрямую, не fallback на pageNumber
          // Если page не передан, используем текущее значение
          const newPage = typeof data.page === 'number' && data.page > 0 ? data.page : pageNumber;
          const newScale = typeof data.scale === 'number' ? data.scale : pdfScale;
          const newPanX = typeof data.panX === 'number' ? data.panX : pdfPanX;
          const newPanY = typeof data.panY === 'number' ? data.panY : pdfPanY;
          
          console.log('Применяем изменения PDF:', { 
            receivedPage: data.page,
            newPage, 
            currentPage: pageNumber, 
            newScale, 
            currentScale: pdfScale,
            pageChanged: newPage !== pageNumber,
            sender,
            currentUser
          });
          
          // Очищаем canvas при смене страницы
          if (newPage !== pageNumber && canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            canvasHistoryRef.current = [];
          }
          
          // Применяем изменения состояния - важно обновить pageNumber первым
          if (newPage !== pageNumber) {
            console.log('Обновляем pageNumber с', pageNumber, 'на', newPage);
            setPageNumber(newPage);
          }
          setPdfScale(newScale);
          setPdfPanX(newPanX);
          setPdfPanY(newPanY);
          
          setTimeout(() => {
            isSyncingRef.current = false;
          }, 200);
        }
      } else if (data.type === 'drawing') {
        // Синхронизация рисования
        handleRemoteDrawing(data);
      } else if (data.type === 'webrtc-offer') {
        // Получен offer от преподавателя, создаем answer
        console.log('Получен WebRTC offer');
        if (user?.user_type === 'student' && peerConnectionRef.current) {
          try {
            await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.data));
            const answer = await peerConnectionRef.current.createAnswer();
            await peerConnectionRef.current.setLocalDescription(answer);
            
            console.log('Отправка WebRTC answer');
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({
                type: 'webrtc-answer',
                data: answer,
              }));
            } else {
              console.error('WebSocket не открыт для отправки answer');
            }
          } catch (error) {
            console.error('Ошибка обработки offer:', error);
          }
        }
      } else if (data.type === 'webrtc-answer') {
        // Получен answer от ученика
        console.log('Получен WebRTC answer');
        if (user?.user_type === 'teacher' && peerConnectionRef.current) {
          try {
            await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.data));
            console.log('Remote description установлен');
          } catch (error) {
            console.error('Ошибка обработки answer:', error);
          }
        }
      } else if (data.type === 'webrtc-ice-candidate') {
        // Получен ICE кандидат
        console.log('Получен ICE кандидат');
        if (peerConnectionRef.current && data.data) {
          try {
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.data));
          } catch (error) {
            console.error('Ошибка добавления ICE кандидата:', error);
          }
        }
      } else if (data.message || data.type === 'chat_message' || data.image) {
        // Обычное сообщение чата или сообщение с изображением
        console.log('Получено сообщение чата:', data);
        const username = data.username || 'Unknown';
        const displayName = data.display_name || username;
        const message = data.message || '';
        const image = data.image || null;
        const currentUsername = user?.username || 'Unknown';
        
        // Игнорируем сообщения от текущего пользователя (они уже добавлены оптимистично)
        if (username === currentUsername) {
          console.log('Игнорируем собственное сообщение, оно уже добавлено оптимистично');
          return;
        }
        
        if (message || image) {
          setMessages(prev => {
            // Проверяем, нет ли уже такого сообщения (избегаем дубликатов)
            const isDuplicate = prev.some(
              msg => msg.username === username && 
              msg.message === message && 
              msg.image === image
            );
            if (isDuplicate) {
              console.log('Дубликат сообщения, пропускаем');
              return prev;
            }
            return [...prev, { username, display_name: displayName, message, image }];
          });
        }
      } else {
        console.log('Неизвестный тип сообщения:', data);
      }
      } catch (error) {
        console.error('Ошибка обработки WebSocket-сообщения:', error, data);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      // Пытаемся переподключиться, если соединение закрылось неожиданно
      if (event.code !== 1000) { // 1000 = нормальное закрытие
        console.log('Попытка переподключения через 3 секунды...');
        setTimeout(() => {
          if (wsRef.current?.readyState === WebSocket.CLOSED) {
            console.log('Переподключение...');
            setupWebSocket();
          }
        }, 3000);
      }
    };

    wsRef.current = ws;
  };

  // Функция для отправки изображения
  const sendImage = (imageData) => {
    const wsState = wsRef.current?.readyState;
    console.log('sendImage called:', { hasImage: !!imageData, wsReady: wsState });
    
    if (!imageData) {
      console.log('Изображение пустое');
      return;
    }
    
    if (!wsRef.current) {
      console.error('WebSocket не инициализирован');
      alert('WebSocket не подключен. Пожалуйста, обновите страницу.');
      return;
    }
    
    // Проверяем состояние WebSocket
    if (wsState === WebSocket.CLOSED || wsState === WebSocket.CLOSING) {
      console.error('WebSocket закрыт или закрывается, состояние:', wsState);
      alert('Соединение потеряно. Пожалуйста, обновите страницу.');
      return;
    }
    
    if (wsState !== WebSocket.OPEN) {
      console.error('WebSocket не подключен, состояние:', wsState);
      alert('Соединение не установлено. Пожалуйста, подождите...');
      return;
    }
    
    try {
      // Оптимистичное обновление UI - показываем сообщение сразу
      const currentUsername = user?.username || 'Unknown';
      const currentDisplayName = (user?.first_name && user?.last_name) 
        ? `${user.first_name} ${user.last_name}`.trim()
        : (user?.first_name || user?.last_name || user?.username || 'Unknown');
      setMessages(prev => [...prev, { username: currentUsername, display_name: currentDisplayName, message: '', image: imageData }]);
      
      const messageData = { 
        type: 'message',
        image: imageData,
        message: '' // Можно добавить опциональный текст
      };
      console.log('Отправка изображения:', { hasImage: !!imageData });
      
      // Дополнительная проверка перед отправкой
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(messageData));
      } else {
        throw new Error('WebSocket закрыт во время отправки');
      }
    } catch (error) {
      console.error('Ошибка отправки изображения:', error);
    }
  };

  // Функция для обработки файла изображения
  const handleImageFile = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Пожалуйста, выберите изображение');
      return;
    }
    
    // Ограничиваем размер (например, 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Размер изображения не должен превышать 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target.result; // Base64 строка
      sendImage(imageData);
    };
    reader.onerror = () => {
      alert('Ошибка чтения файла');
    };
    reader.readAsDataURL(file);
  };

  const sendMessage = () => {
    const trimmedMessage = messageInput.trim();
    const wsState = wsRef.current?.readyState;
    console.log('sendMessage called:', { trimmedMessage, wsReady: wsState });
    
    if (!trimmedMessage) {
      console.log('Сообщение пустое');
      return;
    }
    
    if (!wsRef.current) {
      console.error('WebSocket не инициализирован');
      alert('WebSocket не подключен. Пожалуйста, обновите страницу.');
      return;
    }
    
    // Проверяем состояние WebSocket
    if (wsState === WebSocket.CLOSED || wsState === WebSocket.CLOSING) {
      console.error('WebSocket закрыт или закрывается, состояние:', wsState);
      alert('Соединение потеряно. Пожалуйста, обновите страницу.');
      return;
    }
    
    if (wsState !== WebSocket.OPEN) {
      console.error('WebSocket не подключен, состояние:', wsState);
      alert('Соединение не установлено. Пожалуйста, подождите...');
      return;
    }
    
    try {
      // Оптимистичное обновление UI - показываем сообщение сразу
      const currentUsername = user?.username || 'Unknown';
      const currentDisplayName = (user?.first_name && user?.last_name) 
        ? `${user.first_name} ${user.last_name}`.trim()
        : (user?.first_name || user?.last_name || user?.username || 'Unknown');
      setMessages(prev => [...prev, { username: currentUsername, display_name: currentDisplayName, message: trimmedMessage }]);
      
      const messageData = { 
        type: 'message',
        message: trimmedMessage 
      };
      console.log('Отправка сообщения:', messageData);
      
      // Дополнительная проверка перед отправкой
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(messageData));
        setMessageInput('');
      } else {
        throw new Error('WebSocket закрыт во время отправки');
      }
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      // Убираем сообщение из UI, если отправка не удалась
      setMessages(prev => prev.filter((msg, idx) => {
        const lastMsg = prev[prev.length - 1];
        return !(lastMsg && lastMsg.username === (user?.username || 'Unknown') && lastMsg.message === trimmedMessage);
      }));
      alert('Не удалось отправить сообщение. Пожалуйста, попробуйте еще раз.');
    }
  };

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    console.log('PDF успешно загружен, количество страниц:', numPages);
    console.log('selectedFile:', selectedFile);
    setNumPages(numPages);
    setPageNumber(1);
    
    // Обновляем размеры canvas при загрузке PDF
    // Canvas должен быть достаточно большим для PDF с padding
    if (canvasRef.current && pdfContainerRef.current) {
      const pageWidth = Math.min(pdfContainerRef.current.offsetWidth - 40, 800);
      setPdfPageWidth(pageWidth); // Сохраняем ширину для использования в Page
      const pageHeight = pageWidth * 1.414; // A4 соотношение сторон
      const padding = 100; // Увеличенный отступ со всех сторон
      // Увеличиваем canvas в 2 раза для большей области рисования
      canvasRef.current.width = (pageWidth + (padding * 2)) * 2;
      canvasRef.current.height = (pageHeight + (padding * 2)) * 2;
      
      // Центрируем PDF по оси X
      const centerX = calculateCenterX(pageWidth);
      setPdfPanX(centerX);
      setPdfPanY(0); // Сбрасываем Y
    }
  }, [selectedFile]);

  // Функция для вычисления центрирования по оси X
  const calculateCenterX = useCallback((pageWidth) => {
    if (!pdfContainerRef.current) return 0;
    // PDF имеет padding 20px, canvas имеет padding 100px
    // Ширина PDF с padding: pageWidth + 40 (20px с каждой стороны)
    // Ширина контейнера: pdfContainerRef.current.offsetWidth
    // Центрирование: (ширина контейнера - ширина PDF) / 2
    const containerWidth = pdfContainerRef.current.offsetWidth;
    const pdfWidthWithPadding = pageWidth + 40; // 20px padding с каждой стороны
    const centerOffset = (containerWidth - pdfWidthWithPadding) / 2;
    // Учитываем разницу padding между PDF (20px) и canvas (100px)
    const canvasPaddingDiff = 100 - 20; // 80px
    return centerOffset - canvasPaddingDiff;
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error('Ошибка загрузки PDF:', error);
    console.error('URL файла:', selectedFile);
    alert('Ошибка загрузки PDF файла. Проверьте консоль для деталей.');
  }, [selectedFile]);

  const changePage = (offset) => {
    const newPage = Math.max(1, Math.min(numPages, pageNumber + offset));
    
    // Предотвращаем циклическую синхронизацию
    isSyncingRef.current = true;
    
    setPageNumber(newPage);
    
    // Очищаем canvas при смене страницы
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      // Очищаем историю при смене страницы
      canvasHistoryRef.current = [];
    }
    
    // Отправляем команду очистки через WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'drawing',
        action: 'clear',
      }));
    }
    
    // Синхронизируем смену страницы (только для преподавателя)
    // Временно снимаем флаг синхронизации для отправки
    if (user?.user_type === 'teacher' && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const wasSyncing = isSyncingRef.current;
      isSyncingRef.current = false; // Временно снимаем для отправки
      syncPdfState(newPage, pdfScale, pdfPanX, pdfPanY);
      isSyncingRef.current = wasSyncing; // Восстанавливаем
    }
    
    // Снимаем флаг синхронизации после небольшой задержки
    setTimeout(() => {
      isSyncingRef.current = false;
    }, 200);
  };

  // Синхронизация состояния PDF
  const syncPdfState = (page, scale, panX, panY) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isSyncingRef.current) {
      const stateToSend = {
        type: 'pdf_state',
        page: page,
        scale: scale,
        panX: panX,
        panY: panY,
      };
      console.log('Отправка pdf_state:', stateToSend);
      wsRef.current.send(JSON.stringify(stateToSend));
    }
  };

  // Обработка zoom
  const handleZoom = (delta) => {
    if (user?.user_type !== 'teacher') return;
    
    const newScale = Math.max(0.5, Math.min(3.0, pdfScale + delta));
    setPdfScale(newScale);
    syncPdfState(pageNumber, newScale, pdfPanX, pdfPanY);
  };

  // Обработка pan (перемещение)
  const handlePan = (deltaX, deltaY) => {
    if (user?.user_type !== 'teacher') return;
    
    const newPanX = pdfPanX + deltaX;
    const newPanY = pdfPanY + deltaY;
    setPdfPanX(newPanX);
    setPdfPanY(newPanY);
    syncPdfState(pageNumber, pdfScale, newPanX, newPanY);
  };

  // Обработка рисования от удаленного пользователя
  const handleRemoteDrawing = (data) => {
    if (!canvasRef.current) return;

    // Проверяем, не является ли это нашим собственным сообщением
    // Если sender совпадает с текущим пользователем, игнорируем
    if (data.sender && data.sender === user?.username) {
      return; // Это наше собственное сообщение, не обрабатываем
    }

    const ctx = canvasRef.current.getContext('2d');

    // Координаты приходят относительно PDF
    // PDF контент начинается в точке (100+20, 100+20) = (120, 120) на canvas
    // Координата на canvas = координата на PDF + 120
    const canvasPadding = 100; // Отступ canvas
    const pdfPadding = 20; // Отступ PDF
    const x = data.x + (canvasPadding + pdfPadding);
    const y = data.y + (canvasPadding + pdfPadding);

    // Получаем или создаем состояние пути для этого пользователя
    const senderId = data.sender || 'unknown';
    if (!remoteDrawingStateRef.current[senderId]) {
      remoteDrawingStateRef.current[senderId] = { isActive: false };
    }
    const drawingState = remoteDrawingStateRef.current[senderId];

    if (data.action === 'start') {
      // Сохраняем состояние canvas перед началом рисования от удаленного пользователя
      // Это нужно для синхронной отмены
      if (canvasRef.current) {
        const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
        canvasHistoryRef.current.push(imageData);
        // Ограничиваем размер истории
        if (canvasHistoryRef.current.length > maxHistorySize) {
          canvasHistoryRef.current.shift();
        }
      }
      
      // Начинаем новый путь для плавной линии
      drawingState.isActive = true;
      // Проверяем, является ли это ластиком
      const isEraser = data.isEraser === true || (data.lineWidth || 2) > 10;
      if (isEraser) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = data.color || '#000000';
      }
      ctx.lineWidth = data.lineWidth || 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(x, y);
      drawingState.lastX = x;
      drawingState.lastY = y;
    } else if (data.action === 'draw' && drawingState.isActive) {
      // Соединяем точки плавной линией
      // Проверяем, является ли это ластиком
      const isEraser = data.isEraser === true || (data.lineWidth || 2) > 10;
      if (isEraser) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = data.color || '#000000';
      }
      ctx.lineWidth = data.lineWidth || 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(drawingState.lastX || x, drawingState.lastY || y);
      ctx.lineTo(x, y);
      ctx.stroke();
      // Обновляем последнюю точку
      drawingState.lastX = x;
      drawingState.lastY = y;
    } else if (data.action === 'end' && drawingState.isActive) {
      // Проверяем, является ли это ластиком
      const isEraser = data.isEraser === true || (data.lineWidth || 2) > 10;
      if (isEraser) {
        ctx.globalCompositeOperation = 'destination-out';
      } else {
        ctx.globalCompositeOperation = 'source-over';
      }
      // Если не было движения (простой клик), рисуем точку
      if (data.hasMoved === false) {
        if (isEraser) {
          ctx.fillStyle = 'rgba(0,0,0,1)';
        } else {
          ctx.fillStyle = data.color || '#000000';
        }
        ctx.beginPath();
        ctx.arc(x, y, (data.lineWidth || 2) / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Завершаем линию - рисуем последний сегмент
        if (isEraser) {
          ctx.strokeStyle = 'rgba(0,0,0,1)';
        } else {
          ctx.strokeStyle = data.color || '#000000';
        }
        ctx.lineWidth = data.lineWidth || 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(drawingState.lastX || x, drawingState.lastY || y);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
      ctx.globalCompositeOperation = 'source-over';
      drawingState.isActive = false;
    } else if (data.action === 'clear') {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      // Очищаем состояние для всех пользователей
      remoteDrawingStateRef.current = {};
      // Очищаем историю
      canvasHistoryRef.current = [];
    } else if (data.action === 'undo') {
      // Отмена последнего действия для удаленного пользователя
      if (canvasHistoryRef.current.length > 0) {
        const previousState = canvasHistoryRef.current.pop();
        ctx.putImageData(previousState, 0, 0);
      }
    }
  };

  // Обработка рисования локально
  const handleDrawing = (e, action) => {
    if (!canvasRef.current || !drawingTool || !pdfWrapperRef.current) return;
    
    // Получаем координаты мыши относительно canvas с учетом его transform
    // Canvas имеет transform: scale(pdfScale) translate(pdfPanX, pdfPanY) с transformOrigin: top left
    // Canvas имеет position: absolute, top: 0, left: 0 относительно pdf-wrapper
    
    // 1. Получаем позицию canvas относительно viewport (getBoundingClientRect учитывает transform)
    const canvasRect = canvasRef.current.getBoundingClientRect();
    
    // 2. Координаты мыши относительно viewport
    const mouseX = e.clientX;
    const mouseY = e.clientY;
    
    // 3. Координаты мыши относительно canvas (уже с учетом transform через getBoundingClientRect)
    const relativeX = mouseX - canvasRect.left;
    const relativeY = mouseY - canvasRect.top;
    
    // 4. Обращаем transform canvas
    // Canvas имеет transform: scale(pdfScale) translate(pdfPanX, pdfPanY)
    // getBoundingClientRect() возвращает координаты с учетом transform
    // Но нам нужны координаты на самом canvas (без transform)
    // Используем соотношение размеров для правильного преобразования
    const canvasWidth = canvasRef.current.width;
    const canvasHeight = canvasRef.current.height;
    // canvasRect.width и canvasRect.height - это размеры canvas с учетом transform
    // canvasWidth и canvasHeight - это реальные размеры canvas
    // Координаты относительно canvas без transform
    const x = (relativeX / canvasRect.width) * canvasWidth;
    const y = (relativeY / canvasRect.height) * canvasHeight;
    
    // 5. Координаты для рисования на canvas
    const finalX = x;
    const finalY = y;
    
    // 6. Координаты относительно PDF (для отправки через WebSocket)
    // Canvas имеет padding 100px, PDF имеет padding 20px
    // PDF контент начинается в точке (100+20, 100+20) = (120, 120) на canvas
    // Координата на PDF = координата на canvas - 120
    const pdfPadding = 20;
    const canvasPadding = 100;
    const pdfX = x - (canvasPadding + pdfPadding);
    const pdfY = y - (canvasPadding + pdfPadding);
    
    const ctx = canvasRef.current.getContext('2d');
    
    if (action === 'start') {
      // Сохраняем состояние canvas перед началом рисования
      if (canvasRef.current) {
        const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
        canvasHistoryRef.current.push(imageData);
        // Ограничиваем размер истории
        if (canvasHistoryRef.current.length > maxHistorySize) {
          canvasHistoryRef.current.shift();
        }
      }
      
      isDrawingRef.current = true;
      hasMovedRef.current = false; // Сбрасываем флаг движения
      lastDrawPointRef.current = { x: finalX, y: finalY };
      startPointRef.current = { x: finalX, y: finalY }; // Сохраняем начальную точку
      
      if (drawingTool === 'pen') {
        ctx.strokeStyle = drawingColor;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        // Начинаем новый путь
        ctx.beginPath();
        ctx.moveTo(finalX, finalY);
      } else if (drawingTool === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
        ctx.lineWidth = eraserSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        // Начинаем новый путь
        ctx.beginPath();
        ctx.moveTo(finalX, finalY);
      }
      
      // Отправляем начало рисования (координаты относительно PDF)
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'drawing',
          action: 'start',
          x: pdfX, // Координаты относительно PDF
          y: pdfY,
          color: drawingColor,
          lineWidth: drawingTool === 'eraser' ? eraserSize : lineWidth,
          isEraser: drawingTool === 'eraser', // Явный флаг для ластика
        }));
      }
    } else if (action === 'draw' && isDrawingRef.current) {
      hasMovedRef.current = true; // Отмечаем, что было движение
      
      if (drawingTool === 'pen' || drawingTool === 'eraser') {
        // Соединяем предыдущую точку с текущей плавной линией
        // Используем lineTo для плавного соединения точек
        ctx.lineTo(finalX, finalY);
        ctx.stroke();
        // Начинаем новый путь от текущей точки для следующего сегмента
        ctx.beginPath();
        ctx.moveTo(finalX, finalY);
      }
      
      // Отправляем продолжение рисования
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'drawing',
          action: 'draw',
          x: pdfX, // Координаты относительно PDF
          y: pdfY,
          color: drawingColor,
          lineWidth: drawingTool === 'eraser' ? eraserSize : lineWidth,
          isEraser: drawingTool === 'eraser', // Явный флаг для ластика
        }));
      }
      
      // Обновляем последнюю точку
      lastDrawPointRef.current = { x: finalX, y: finalY };
    } else if (action === 'end') {
      if (isDrawingRef.current) {
        // Сохраняем значение hasMoved перед сбросом
        const wasMoved = hasMovedRef.current;
        
        // Если не было движения (простой клик), рисуем точку
        if (!wasMoved) {
          if (drawingTool === 'pen') {
            ctx.fillStyle = drawingColor;
            ctx.beginPath();
            ctx.arc(finalX, finalY, lineWidth / 2, 0, Math.PI * 2);
            ctx.fill();
          } else if (drawingTool === 'eraser') {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = 'rgba(0,0,0,1)';
            ctx.beginPath();
            ctx.arc(finalX, finalY, eraserSize / 2, 0, Math.PI * 2);
            ctx.fill();
          }
        } else {
          // Завершаем линию - рисуем последний сегмент
          if (drawingTool === 'pen' || drawingTool === 'eraser') {
            ctx.lineTo(finalX, finalY);
            ctx.stroke();
          }
        }
        
        ctx.globalCompositeOperation = 'source-over';
        isDrawingRef.current = false;
        hasMovedRef.current = false;
        
        // Отправляем окончание рисования
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'drawing',
            action: 'end',
            x: pdfX, // Координаты относительно PDF
            y: pdfY,
            color: drawingColor,
            lineWidth: drawingTool === 'eraser' ? eraserSize : lineWidth,
            hasMoved: wasMoved,
            isEraser: drawingTool === 'eraser', // Явный флаг для ластика
          }));
        }
      }
    }
  };

  // Отмена последнего действия
  const undoLastAction = () => {
    if (!canvasRef.current || canvasHistoryRef.current.length === 0) return;
    
    const ctx = canvasRef.current.getContext('2d');
    // Восстанавливаем предыдущее состояние
    const previousState = canvasHistoryRef.current.pop();
    ctx.putImageData(previousState, 0, 0);
    
    // Отправляем команду отмены через WebSocket для синхронизации со всеми участниками
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'drawing',
        action: 'undo',
      }));
    }
  };

  // Очистка canvas
  const clearCanvas = () => {
    if (!canvasRef.current) return;
    
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    // Очищаем историю
    canvasHistoryRef.current = [];
    
    // Отправляем команду очистки
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'drawing',
        action: 'clear',
      }));
    }
  };

  const handleFileSelect = async (file) => {
    console.log('Выбран файл:', file);
    
    // Проверяем, что file.file_url или file.file существует
    // file_url - это вычисляемое поле из сериализатора
    let fileUrl = file.file_url || file.file;
    
    // Если fileUrl все еще null, используем fallback - API endpoint для получения файла
    if (!fileUrl && file.id) {
      console.log('file_url и file равны null, используем API endpoint');
      // Используем API endpoint для получения файла
      fileUrl = `/api/lessons/files/${file.id}/download/`;
      console.log('Сформирован fallback URL через API:', fileUrl);
    } else if (!fileUrl && file.name) {
      // Если нет id, используем старый способ (для обратной совместимости)
      console.log('file_url и file равны null, нет id, используем fallback URL из имени файла');
      fileUrl = `/media/lessons/${encodeURIComponent(file.name)}`;
      console.log('Сформирован fallback URL:', fileUrl);
    }
    
    // Если fileUrl все еще null, выводим ошибку
    if (!fileUrl) {
      console.error('Не удалось получить URL файла:', file);
      alert('Ошибка: не удалось получить URL файла. Проверьте консоль для деталей.');
      return;
    }
    
    // Убеждаемся, что URL правильный
    // Если это относительный путь, который начинается с /api/ или /media/, оставляем как есть
    // Если это полный URL, используем его
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      // Это абсолютный URL, используем как есть
      console.log('Используется абсолютный URL:', fileUrl);
    } else if (fileUrl.startsWith('/api/') || fileUrl.startsWith('/media/')) {
      // Это правильный относительный путь через API или медиа
      console.log('Используется относительный URL:', fileUrl);
    } else if (!fileUrl.startsWith('/')) {
      // Если не начинается с /, добавляем
      fileUrl = '/' + fileUrl;
      console.log('Добавлен / к URL:', fileUrl);
    }
    
    console.log('Итоговый URL файла для react-pdf:', fileUrl);
    setSelectedFile(fileUrl);
    setPageNumber(1);
    setNumPages(null);
    setPdfScale(1.0);
    setPdfPanX(0);
    setPdfPanY(0);
    
    // Очищаем canvas при смене файла
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      // Очищаем историю при смене файла
      canvasHistoryRef.current = [];
    }
    
    // Отправляем информацию о выбранном файле через WebSocket (только для преподавателей)
    if (user?.user_type === 'teacher' && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'file_selected',
        fileUrl: fileUrl,
        fileName: file.name
      }));
      console.log('Отправлено через WebSocket:', { type: 'file_selected', fileUrl, fileName: file.name });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('name', file.name);
    formData.append('file', file);

    try {
      const response = await api.post('/lessons/files/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const newFile = response.data;
      // Добавляем файл в список
      setLessonFiles(prev => [...prev, newFile]);
      // Устанавливаем загруженный файл для просмотра (используем file_url или file)
      const fileUrl = newFile.file_url || newFile.file;
      setSelectedFile(fileUrl);
      setPageNumber(1);
      setNumPages(null); // Сбрасываем количество страниц для нового файла
      
      // Отправляем информацию о загруженном файле через WebSocket
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'file_selected',
          fileUrl: fileUrl,
          fileName: newFile.name
        }));
      }
      
      // Сбрасываем input для возможности загрузки того же файла снова
      e.target.value = '';
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      alert('Ошибка загрузки файла: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleFileDelete = async (fileId, e) => {
    e.stopPropagation(); // Предотвращаем выбор файла при клике на кнопку удаления
    
    if (!window.confirm('Вы уверены, что хотите удалить этот файл?')) {
      return;
    }

    try {
      await api.delete(`/lessons/files/${fileId}/`);
      // Удаляем файл из списка
      setLessonFiles(prev => prev.filter(file => file.id !== fileId));
      
      // Если удаленный файл был выбран, сбрасываем выбор
      const deletedFile = lessonFiles.find(f => f.id === fileId);
      if (deletedFile && (selectedFile === (deletedFile.file_url || deletedFile.file))) {
        setSelectedFile(null);
        setPageNumber(1);
        setNumPages(null);
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          canvasHistoryRef.current = [];
        }
      }
    } catch (error) {
      console.error('Ошибка удаления файла:', error);
      alert('Ошибка удаления файла: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="classroom">
      <Header pageTitle="Класс" />
      <div className="classroom-content">
        <div className="videos-section">
          {/* Всегда сначала преподаватель, потом ученик */}
          <div className="video-container">
              <h3>Преподаватель{user?.user_type === 'teacher' ? (() => {
                const displayName = (user?.first_name && user?.last_name) 
                  ? `${user.first_name} ${user.last_name}`.trim()
                  : (user?.first_name || user?.last_name || user?.username || '');
                return displayName ? ` (${displayName})` : '';
              })() : ''}</h3>
              {user?.user_type === 'teacher' ? (
                <>
                  <video ref={localVideoRef} autoPlay muted className="video-player" />
                  <div className="video-controls">
                    <button 
                      onClick={toggleVideo}
                      className={`video-control-button ${!isVideoEnabled ? 'disabled' : ''}`}
                      title={isVideoEnabled ? 'Отключить видео' : 'Включить видео'}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="currentColor"/>
                        {!isVideoEnabled && <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>}
                      </svg>
                    </button>
                    <button 
                      onClick={toggleAudio}
                      className={`video-control-button ${!isAudioEnabled ? 'disabled' : ''}`}
                      title={isAudioEnabled ? 'Отключить микрофон' : 'Включить микрофон'}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z" fill="currentColor"/>
                        <path d="M19 10V12C19 15.87 15.87 19 12 19C8.13 19 5 15.87 5 12V10H7V12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12V10H19Z" fill="currentColor"/>
                        <path d="M12 19V23H8V21H12V19Z" fill="currentColor"/>
                        {!isAudioEnabled && <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>}
                      </svg>
                    </button>
                  </div>
                </>
              ) : (
                <video ref={remoteVideoRef} autoPlay className="video-player" />
              )}
            </div>
            <div className="video-container">
              <h3>Ученик{(() => {
                if (user?.user_type !== 'student') return '';
                const displayName = (user?.first_name && user?.last_name) 
                  ? `${user.first_name} ${user.last_name}`.trim()
                  : (user?.first_name || user?.last_name || user?.username || '');
                return displayName ? ` (${displayName})` : '';
              })()}</h3>
              {user?.user_type === 'student' ? (
                <>
                  <video ref={localVideoRef} autoPlay muted className="video-player" />
                  <div className="video-controls">
                    <button 
                      onClick={toggleVideo}
                      className={`video-control-button ${!isVideoEnabled ? 'disabled' : ''}`}
                      title={isVideoEnabled ? 'Отключить видео' : 'Включить видео'}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="currentColor"/>
                        {!isVideoEnabled && <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>}
                      </svg>
                    </button>
                    <button 
                      onClick={toggleAudio}
                      className={`video-control-button ${!isAudioEnabled ? 'disabled' : ''}`}
                      title={isAudioEnabled ? 'Отключить микрофон' : 'Включить микрофон'}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z" fill="currentColor"/>
                        <path d="M19 10V12C19 15.87 15.87 19 12 19C8.13 19 5 15.87 5 12V10H7V12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12V10H19Z" fill="currentColor"/>
                        <path d="M12 19V23H8V21H12V19Z" fill="currentColor"/>
                        {!isAudioEnabled && <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>}
                      </svg>
                    </button>
                  </div>
                </>
              ) : (
                <video ref={remoteVideoRef} autoPlay className="video-player" />
              )}
            </div>
        </div>
        <div className="classroom-main">
          <div className="pdf-viewer">
            {user?.user_type === 'teacher' && (
              <div className="classroom-content-tabs">
                <button
                  type="button"
                  className={`classroom-content-tab ${contentTab === 'materials' ? 'active' : ''}`}
                  onClick={() => switchContentTab('materials')}
                >
                  Материалы урока
                </button>
                <button
                  type="button"
                  className={`classroom-content-tab ${contentTab === 'files' ? 'active' : ''}`}
                  onClick={() => switchContentTab('files')}
                >
                  Файлы урока
                </button>
              </div>
            )}

            {contentTab === 'materials' ? (
              <div className="lesson-materials-panel">
                {selectedMaterial && Array.isArray(selectedMaterial.content) ? (
                  <InteractiveMaterial
                    key={selectedMaterial.id}
                    materialId={selectedMaterial.id}
                    title={selectedMaterial.title}
                    content={selectedMaterial.content}
                    progress={materialProgress}
                    onProgressChange={handleMaterialProgressChange}
                  />
                ) : selectedMaterial ? (
                  <div className="lesson-materials-empty">
                    <span className="lesson-materials-empty-icon">📚</span>
                    <p>Не удалось загрузить содержимое материала</p>
                  </div>
                ) : lessonMaterials.length === 0 ? (
                  <div className="lesson-materials-empty">
                    <span className="lesson-materials-empty-icon">📚</span>
                    <p>Материалы урока пока не добавлены</p>
                  </div>
                ) : (
                  <div className="lesson-materials-empty">
                    <span className="lesson-materials-empty-icon">📚</span>
                    <p>Выберите материал для просмотра</p>
                  </div>
                )}
              </div>
            ) : selectedFile ? (
              <div className="pdf-container" ref={pdfContainerRef}>
                {/* Панель инструментов - выше PDF/canvas */}
                <div className="pdf-controls">
                  <div className="pdf-controls-left">
                    {/* Кнопки навигации только для преподавателя */}
                    {user?.user_type === 'teacher' && (
                      <>
                        <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} title="Предыдущая страница">
                          ←
                        </button>
                        <span>Страница {pageNumber} из {numPages}</span>
                        <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} title="Следующая страница">
                          →
                        </button>
                      </>
                    )}
                    {/* Для ученика показываем только номер страницы */}
                    {user?.user_type === 'student' && (
                      <span>Страница {pageNumber} из {numPages}</span>
                    )}
                  </div>
                  <div className="pdf-controls-right">
                    {/* Инструменты управления (только для преподавателя) */}
                    {user?.user_type === 'teacher' && (
                      <>
                        <button onClick={() => handleZoom(0.1)} title="Увеличить">
                          🔍+
                        </button>
                        <button onClick={() => handleZoom(-0.1)} title="Уменьшить">
                          🔍-
                        </button>
                        <button onClick={() => { 
                          const centerX = calculateCenterX(pdfPageWidth);
                          setPdfScale(1.0);
                          setPdfPanX(centerX);
                          setPdfPanY(0);
                          syncPdfState(pageNumber, 1.0, centerX, 0);
                        }} title="Сброс">
                          ↺
                        </button>
                      </>
                    )}
                    {/* Инструменты рисования (для всех) */}
                    <div className="drawing-tool-wrapper">
                      <button 
                        onClick={() => {
                          setDrawingTool(drawingTool === 'pen' ? null : 'pen');
                        }}
                        className={drawingTool === 'pen' ? 'active' : ''}
                        title="Карандаш"
                      >
                        ✏️
                      </button>
                      <label className="color-picker-button" title="Цвет карандаша">
                        <input 
                          type="color" 
                          value={drawingColor} 
                          onChange={(e) => {
                            setDrawingColor(e.target.value);
                          }}
                          className="color-picker-hidden"
                        />
                        <span className="color-picker-preview" style={{ backgroundColor: drawingColor }}></span>
                      </label>
                      <input 
                        type="range" 
                        min="1" 
                        max="10" 
                        value={lineWidth} 
                        onChange={(e) => setLineWidth(parseInt(e.target.value))}
                        className="line-width-slider"
                        title="Толщина линии"
                      />
                    </div>
                    <div className="drawing-tool-wrapper">
                      <button 
                        onClick={() => setDrawingTool(drawingTool === 'eraser' ? null : 'eraser')}
                        className={drawingTool === 'eraser' ? 'active' : ''}
                        title="Ластик"
                      >
                        🧹
                      </button>
                      <input 
                        type="range" 
                        min="10" 
                        max="50" 
                        value={eraserSize} 
                        onChange={(e) => setEraserSize(parseInt(e.target.value))}
                        className="line-width-slider"
                        title="Размер ластика"
                      />
                    </div>
                    <button onClick={undoLastAction} title="Отменить последнее действие">
                      ↶
                    </button>
                    <button onClick={clearCanvas} title="Очистить все">
                      🗑️
                    </button>
                  </div>
                </div>
                {/* Контейнер для PDF и canvas с overflow */}
                <div 
                  ref={pdfWrapperRef}
                  className={`pdf-wrapper ${drawingTool === 'eraser' ? 'eraser-active' : ''}`}
                  style={{
                    cursor: drawingTool === 'eraser' ? 'none' : (drawingTool ? 'crosshair' : (user?.user_type === 'teacher' ? 'move' : 'default')),
                    '--mouse-x': drawingTool === 'eraser' ? `${mousePosition.x}px` : '0',
                    '--mouse-y': drawingTool === 'eraser' ? `${mousePosition.y}px` : '0',
                    '--eraser-size': `${eraserSize}px`,
                  }}
                  onMouseMove={(e) => {
                    // Обновляем позицию мыши для индикатора ластика
                    if (drawingTool === 'eraser' && pdfWrapperRef.current) {
                      const wrapperRect = pdfWrapperRef.current.getBoundingClientRect();
                      setMousePosition({
                        x: e.clientX - wrapperRect.left,
                        y: e.clientY - wrapperRect.top,
                      });
                    }
                    // Обрабатываем рисование
                    if (isDrawingRef.current && drawingTool) {
                      handleDrawing(e, 'draw');
                    }
                  }}
                  onMouseDown={(e) => {
                    if (drawingTool) {
                      handleDrawing(e, 'start');
                    } else if (user?.user_type === 'teacher' && e.button === 0) {
                      // Начало pan для преподавателя
                      const startX = e.clientX - pdfPanX;
                      const startY = e.clientY - pdfPanY;
                      const handleMouseMove = (e2) => {
                        const newPanX = e2.clientX - startX;
                        const newPanY = e2.clientY - startY;
                        setPdfPanX(newPanX);
                        setPdfPanY(newPanY);
                        syncPdfState(pageNumber, pdfScale, newPanX, newPanY);
                      };
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }
                  }}
                  onMouseUp={(e) => {
                    if (isDrawingRef.current && drawingTool) {
                      handleDrawing(e, 'end');
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isDrawingRef.current && drawingTool) {
                      handleDrawing(e, 'end');
                    }
                  }}
                >
                  {/* PDF как фон - вынесен в отдельный мемоизированный компонент */}
                  {/* Не перерендеривается при изменении pan/scale, только при смене file/pageNumber/pageWidth */}
                  {selectedFile && (
                    <PdfViewer
                      ref={pdfBackgroundRef}
                      file={selectedFile}
                      pageNumber={pageNumber}
                      pageWidth={pdfPageWidth}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                    />
                  )}
                  {/* Canvas поверх PDF - transform обновляется через useEffect */}
                  <canvas
                    ref={canvasRef}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      pointerEvents: drawingTool ? 'auto' : 'none',
                      zIndex: 10,
                      transformOrigin: 'top left',
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="pdf-placeholder">
                {user?.user_type === 'teacher' 
                  ? 'Выберите файл справа для просмотра'
                  : 'Ожидание выбора файла преподавателем...'}
              </div>
            )}
          </div>
        </div>
        <div className="classroom-sidebar">
          <div className="chat-section">
            <h3>Чат</h3>
            <div className="chat-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className="chat-message">
                  <strong>{msg.display_name || msg.username}: </strong>
                  {msg.image && (
                    <div className="chat-image-container">
                      <img 
                        src={msg.image} 
                        alt="Изображение из чата" 
                        className="chat-image"
                        onClick={() => setSelectedImage(msg.image)}
                      />
                    </div>
                  )}
                  {msg.message && <span>{msg.message}</span>}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className="chat-input-wrapper">
              <input
                type="file"
                ref={imageInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleImageFile(file);
                  }
                  // Сбрасываем input для возможности повторного выбора того же файла
                  e.target.value = '';
                }}
              />
              <div className="chat-input">
                <div 
                  className="chat-icon chat-attach-icon"
                  onClick={() => imageInputRef.current?.click()}
                  title="Добавить файл"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                    <polyline points="21 15 16 10 5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  onPaste={(e) => {
                    const items = e.clipboardData.items;
                    for (let i = 0; i < items.length; i++) {
                      if (items[i].type.indexOf('image') !== -1) {
                        e.preventDefault();
                        const file = items[i].getAsFile();
                        if (file) {
                          handleImageFile(file);
                        }
                        return;
                      }
                    }
                  }}
                  placeholder="Введите сообщение"
                  className="chat-input-field"
                />
                <div 
                  className="chat-icon chat-send-icon"
                  onClick={sendMessage}
                  title="Отправить"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            {/* Модальное окно для просмотра изображения */}
            {selectedImage && (
              <div 
                className="image-modal" 
                onClick={() => setSelectedImage(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSelectedImage(null);
                  }
                }}
                tabIndex={-1}
              >
                <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                  <button 
                    className="image-modal-close"
                    onClick={() => setSelectedImage(null)}
                  >
                    ×
                  </button>
                  <img src={selectedImage} alt="Полноразмерное изображение" />
                </div>
              </div>
            )}
          </div>
          {user?.user_type === 'teacher' && contentTab === 'materials' && (
            <div className="files-section materials-section">
              <h3>Материалы урока</h3>
              <div className="material-upload-actions">
                <button
                  type="button"
                  className="btn-upload"
                  onClick={() => setShowMaterialForm((prev) => !prev)}
                >
                  {showMaterialForm ? 'Скрыть форму' : 'Создать материал'}
                </button>
                <input
                  ref={materialFileInputRef}
                  type="file"
                  id="material-file-upload"
                  accept=".txt,text/plain"
                  onChange={handleMaterialFileUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="material-file-upload" className="btn-upload btn-upload-secondary">
                  Загрузить готовый .txt
                </label>
              </div>

              {showMaterialForm && (
                <div className="material-form">
                  <input
                    type="text"
                    className="material-form-title"
                    placeholder="Название материала"
                    value={newMaterialTitle}
                    onChange={(e) => setNewMaterialTitle(e.target.value)}
                  />
                  <textarea
                    ref={materialTextareaRef}
                    className="material-form-text"
                    placeholder="Вставьте текст урока..."
                    value={newMaterialText}
                    onChange={(e) => setNewMaterialText(e.target.value)}
                    rows={8}
                  />
                  <p className="material-form-hint">
                    Формат тегов описан в{' '}
                    <a href="/examples/lesson-material-example.txt" download className="material-form-link">
                      примере файла
                    </a>
                    . Первая строка файла может быть названием: <code># Название</code>
                  </p>
                  <div className="material-form-actions">
                    <button
                      type="button"
                      className="btn-material-tag"
                      onClick={handleMakeTagFromSelection}
                    >
                      Тег: выбор
                    </button>
                    <button
                      type="button"
                      className="btn-material-tag"
                      onClick={handleMakeInputTagFromSelection}
                    >
                      Тег: ввод
                    </button>
                    <button
                      type="button"
                      className="btn-material-tag"
                      onClick={handleMakeOrderTagFromSelection}
                    >
                      Тег: порядок
                    </button>
                    <button
                      type="button"
                      className="btn-material-tag"
                      onClick={handleMakeCheckboxTag}
                    >
                      Тег: чекбокс
                    </button>
                    <button
                      type="button"
                      className="btn-material-save"
                      onClick={handleMaterialSave}
                    >
                      Сохранить
                    </button>
                  </div>
                </div>
              )}

              <div className="files-list">
                {lessonMaterials.length === 0 ? (
                  <p className="files-empty">Нет материалов</p>
                ) : (
                  lessonMaterials.map((material) => (
                    <div
                      key={material.id}
                      className={`file-item ${selectedMaterial?.id === material.id ? 'active' : ''}`}
                      onClick={() => handleMaterialSelect(material)}
                    >
                      <span className="file-name">{material.title}</span>
                      <button
                        className="file-delete-btn"
                        onClick={(e) => handleMaterialDelete(material.id, e)}
                        title="Удалить материал"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          {user?.user_type === 'teacher' && contentTab === 'files' && (
            <div className="files-section">
              <h3>Файлы урока</h3>
              <div className="file-upload">
                <input
                  type="file"
                  id="file-upload"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" className="btn-upload">
                  Загрузить PDF
                </label>
              </div>
              <div className="files-list">
                {lessonFiles.length === 0 ? (
                  <p className="files-empty">Нет доступных файлов</p>
                ) : (
                  lessonFiles.map((file) => (
                    <div
                      key={file.id}
                      className={`file-item ${selectedFile === (file.file_url || file.file) ? 'active' : ''}`}
                      onClick={() => handleFileSelect(file)}
                    >
                      <span className="file-name">{file.name}</span>
                      <button
                        className="file-delete-btn"
                        onClick={(e) => handleFileDelete(file.id, e)}
                        title="Удалить файл"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Classroom;

